/**
 * Utilitários para serialização/desserialização de URL search params.
 * Genéricos — reutilizáveis por qualquer feature com paginação/filtros.
 */

/**
 * Lê os search params da URL e retorna um objeto tipado
 * contendo apenas as chaves solicitadas que possuem valor.
 */
export const parseSearchParams = <T>(
  searchParams: URLSearchParams,
  keys: (keyof T & string)[],
): Partial<T> => {
  const result: Record<string, string> = {};

  for (const key of keys) {
    const value = searchParams.get(key);
    if (value !== null && value !== "") {
      result[key] = value;
    }
  }

  return result as Partial<T>;
};

/**
 * Faz merge dos novos params com os params existentes da URL,
 * removendo chaves com valor vazio, undefined ou null.
 * Retorna um novo URLSearchParams pronto para setSearchParams.
 */
export const serializeToSearchParams = (
  newParams: Record<string, unknown>,
  existingParams: URLSearchParams,
): URLSearchParams => {
  const merged = new URLSearchParams(existingParams);

  for (const [key, value] of Object.entries(newParams)) {
    if (value === undefined || value === null || value === "") {
      merged.delete(key);
    } else {
      merged.set(key, String(value));
    }
  }

  return merged;
};

/**
 * Converte strings vazias ("") em undefined em todas as chaves de um objeto.
 * Útil para converter dados de formulário (react-hook-form) para filtros da API.
 */
export const cleanEmptyStrings = <T>(obj: T) => {
  const result = {} as Record<string, unknown>;

  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    result[key] = value === "" ? undefined : value;
  }

  return result as { [K in keyof T]: Exclude<T[K], ""> | undefined };
};
